import { useState } from "react"
import { StatusPill, type PillTone } from "../../components/cccd/StatusPill"
import { useNavigate } from "react-router"
import api from "../../lib/api"
import { getPublishErrorMessage } from "../../lib/errors"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { useServerTable } from "../../hooks/useServerTable"
import { RowActionsMenu } from "../../components/admin/RowActionsMenu"
import { ConfirmDialog } from "../../components/admin/ConfirmDialog"
import { EyeIcon, EyeCloseIcon, TrashBinIcon } from "../../icons"
import { COURSE_CATEGORIES } from "../../config/courseCategories"

type Difficulty = "beginner" | "intermediate" | "advanced"

type Course = {
  id: string
  title: string
  category?: string | null
  tags?: string | null
  description?: string | null
  shortDescription?: string | null
  difficultyLevel: Difficulty
  isFree: boolean
  /** Decimal serializado por el API (string o número). */
  price?: number | string | null
  isPublished: boolean
  estimatedHours?: number | null
  lesson_count?: number
  student_count?: number
}

function difficultyTone(level: Difficulty): PillTone {
  if (level === "advanced") return "warning"
  if (level === "intermediate") return "brand"
  return "success"
}

function difficultyLabel(level: Difficulty): string {
  if (level === "advanced") return "Avanzado"
  if (level === "intermediate") return "Intermedio"
  return "Básico"
}

export default function CoursesPage() {
  const navigate = useNavigate()

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null)
  const [togglingId, setTogglingId] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | "published" | "draft">("all")  const {
    rows: courses,
    total: coursesTotal,
    payload,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    error: loadError,
    reload,
  } = useServerTable<Course>({
    endpoint: "/academy/courses/admin/all",
    defaultPageSize: 20,
    defaultSort: { column: "createdAt", dir: "desc" },
    params: { search, status: statusFilter === "all" ? undefined : statusFilter },
    extractRows: (data) => data.courses ?? [],
    extractTotal: (data) => data.pagination?.total ?? 0,
  })

  // Métricas agregadas: vienen del servidor (totales globales), no se calculan con la página cargada.
  const summary = (payload as { summary?: { total: number; published: number; draft: number; free: number } } | undefined)?.summary ?? {
    total: 0,
    published: 0,
    draft: 0,
    free: 0,
  }

  async function onTogglePublish(course: Course) {
    setTogglingId(course.id)
    try {
      await api.put(`/academy/courses/${course.id}`, { is_published: !course.isPublished })
      await reload()
      toastSuccess(course.isPublished ? "Curso despublicado" : "Curso publicado")
    } catch (err) {
      toastError(getPublishErrorMessage(err))
    } finally {
      setTogglingId(null)
    }
  }

  async function onDelete(id: string) {
    setDeletingId(id)
    try {
      await api.delete(`/academy/courses/${id}`)
      await reload()
      toastSuccess("Curso eliminado")
    } catch (err) {
      toastError(err)
    } finally {
      setDeletingId(null)
      setCourseToDelete(null)
    }
  }

  const columns: DataTableColumn<Course>[] = [
    {
      key: "title",
      label: "Curso",
      sortable: true,
      render: (course) => {
        const tags = (course.tags ?? "").split(",").map((t) => t.trim()).filter(Boolean)
        return (
          <>
            <p className="font-medium text-gray-800 dark:text-white/90">{course.title}</p>
            <div className="mt-0.5 flex flex-wrap gap-1">
              {course.category ? (
                <span className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-white/[0.06] dark:text-gray-400">
                  {COURSE_CATEGORIES.find((c) => c.value === course.category)?.label ?? course.category}
                </span>
              ) : null}
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-600 dark:bg-brand-500/10 dark:text-brand-400"
                >
                  {tag}
                </span>
              ))}
            </div>
          </>
        )
      },
    },
    {
      key: "difficultyLevel",
      label: "Dificultad",
      render: (course) => (
        <StatusPill tone={difficultyTone(course.difficultyLevel)} label={difficultyLabel(course.difficultyLevel)} />
      ),
    },
    {
      key: "isPublished",
      label: "Estado",
      sortable: true,
      render: (course) =>
        <StatusPill tone={course.isPublished ? "success" : "gray"} label={course.isPublished ? "Publicado" : "Borrador"} />,
    },
    {
      key: "lesson_count",
      label: "Lecciones",
      align: "right",
      render: (course) => course.lesson_count ?? 0,
    },
    {
      key: "student_count",
      label: "Estudiantes",
      align: "right",
      render: (course) => course.student_count ?? 0,
    },
    {
      key: "price",
      label: "Precio",
      align: "right",
      render: (course) =>
        course.isFree ? (
          <span className="text-success-600 dark:text-success-400">Gratis</span>
        ) : (
          Number(course.price) > 0 ? (
            <span>
              {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(Number(course.price))}
            </span>
          ) : (
            // Curso marcado como de pago pero sin precio: dato incompleto, se muestra como tal en vez de "$0".
            <span className="text-warning-700 dark:text-warning-300">Sin precio</span>
          )
        ),
    },
    {
      key: "actions",
      label: "Acciones",
      align: "right",
      render: (course) => (
        <div onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu
            actions={[
              { label: "Abrir / Editar", icon: <EyeIcon />, to: `/academy/courses/${course.id}` },
              {
                label:
                  togglingId === course.id
                    ? "Actualizando..."
                    : course.isPublished
                    ? "Despublicar"
                    : "Publicar",
                icon: course.isPublished ? <EyeCloseIcon /> : <EyeIcon />,
                onClick: () => onTogglePublish(course),
                disabled: togglingId === course.id,
                requires: "academy:courses:edit",
              },
              {
                label: deletingId === course.id ? "Eliminando..." : "Eliminar",
                icon: <TrashBinIcon />,
                onClick: () => setCourseToDelete(course),
                variant: "danger",
                disabled: deletingId === course.id,
                requires: "academy:courses:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", "Cursos"]}
      title="Cursos"
      subtitle="Catálogo de cursos de la academia"
      tabs={[{ label: "Cursos", to: "/academy/courses", active: true }]}
      metrics={[
        { label: "Total", value: String(summary.total), hint: "Cursos creados en la academia" },
        {
          label: "Publicados",
          value: String(summary.published),
          tone: "good",
          hint: "Visibles para estudiantes ahora mismo",
        },
        {
          label: "Borradores",
          value: String(summary.draft),
          tone: summary.draft > 0 ? "warning" : "neutral",
          hint: "No visibles: falta terminarlos y publicarlos",
        },
        { label: "Gratuitos", value: String(summary.free), hint: "Sin costo de inscripción" },
      ]}
      toolbar={{
        search: {
          value: search,
          onChange: setSearch,
          placeholder: "Buscar cursos...",
        },
        filters: [
          {
            type: "select",
            key: "status",
            label: "Estado",
            value: statusFilter,
            onChange: (value) => setStatusFilter(value as "all" | "published" | "draft"),
            options: [
              { label: "Todos los estados", value: "all" },
              { label: "Publicados", value: "published" },
              { label: "Borradores", value: "draft" },
            ],
          },
        ],
        onRefresh: reload,
        refreshing: loading,        onCreate: () => navigate("/academy/courses/new"),
        createLabel: "+ Nuevo curso",
        requires: "academy:courses:edit",
      }}
    >
      {loadError ? (
        <p className="rounded-xl border border-error-200 bg-error-50 px-4 py-2 text-sm font-medium text-error-600 dark:border-error-500/20 dark:bg-error-500/10 dark:text-error-400">
          {loadError}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={courses}
        rowKey={(course) => course.id}
        loading={loading}
        emptyMessage="No hay cursos."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={coursesTotal}
        onPageChange={setPage}
      />      <ConfirmDialog
        isOpen={courseToDelete !== null}
        title={`¿Eliminar "${courseToDelete?.title ?? ""}"?`}
        message="Se eliminará el curso y sus registros relacionados. Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirming={deletingId === courseToDelete?.id}
        onConfirm={() => courseToDelete && onDelete(courseToDelete.id)}
        onCancel={() => setCourseToDelete(null)}
      />
    </ModuleLayout>
  )
}
